import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { initializeDevCycle } from '@devcycle/js-client-sdk';
import { ExceptionManager } from 'Core/logManager';
import { LogManagerConsole } from '../../core/logManager';

// Context to hold the DevCycle client instance
const FeatureToggleContext = createContext(null);

const buildDevCycleUser = (user, companyId) => {
    const userId = user?.id || user?.username;
    if (!userId) return null;

    LogManagerConsole('User', 'user', user);

    return {
        isAnonymous: false,
        user_id: user?.authId || userId,
        email: user?.email,
        name: user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        privateCustomData: {
            userId,
            emailAddress: user?.email,
            companyId,
            dealerId: user?.dealerId
        }
    };
};

export const FeatureToggleProvider = ({ children }) => {
    const { user, authId } = useSelector(state => state.securityRedux || {});
    const [client, setClient] = useState(null);

    useEffect(() => {
        const companyId = user?.companyId;
        const clientKey = process.env.DEVCYCLE_CLIENT_KEY;

        if (!clientKey || !companyId || !user) {
            return;
        }

        const dvcUser = buildDevCycleUser(user, companyId);
        if (!dvcUser) {
            return;
        }

        try {
            const dvcClient = initializeDevCycle(clientKey, dvcUser);
            setClient(dvcClient);
        } catch (error) {
            ExceptionManager(error, 'Components/FeatureToggle', 'initialize');
        }

        // DevCycle handles reconnection automatically
    }, [user, authId]);

    return (
        <FeatureToggleContext.Provider value={client}>
            {children}
        </FeatureToggleContext.Provider>
    );
};

export const useFeatureToggleContext = () => useContext(FeatureToggleContext);

export const useFeatureValue = (key, defaultValue) => {
    const client = useFeatureToggleContext();
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        if (!client) {
            setValue(defaultValue);
            return;
        }

        // Get initial value once client is ready
        client.onClientInitialized(() => {
            const currentValue = client.variableValue(key, defaultValue);
            setValue(currentValue);
        });

        // Subscribe to updates
        const unsubscribe = client.subscribe(`variableUpdated:${key}`, (_, variable) => {
            setValue(variable?.value ?? defaultValue);
        });

        return () => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        };
    }, [client, key, defaultValue]);

    return value;
};

export const useFeatureEnabled = (key, defaultValue = false) => {
    const value = useFeatureValue(key, defaultValue);
    return Boolean(value);
};

const FeatureToggle = ({
    featureKey,
    defaultValue = false,
    fallback = null,
    children
}) => {
    const enabled = useFeatureEnabled(featureKey, defaultValue);

    if (!enabled) {
        return typeof fallback === 'function' ? fallback() : fallback;
    }

    if (typeof children === 'function') {
        return children(enabled);
    }

    return children ?? null;
};

export default FeatureToggle;
